// GEGENEREERD uit lib/pricing.js — NIET handmatig bewerken.
// Sync: node tools/sync-lib.mjs
(function () {
// Gedeelde prijslogica voor de Huizenga m²-calculator.
// Gebruikt door: de cart-transform-function, de validation-function,
// de theme-JS (via build/copy) en de beheerscripts.
// Puur JS, geen dependencies — draait in Javy/wasm, Node én de browser.

/**
 * Parse een ratio-string naar hoogte÷breedte (decimaal).
 * Accepteert het klantformaat "1 : 0,93" / "1:1", generiek "2:3" (b:h),
 * en een los decimaal getal ("1.282" of "1,282").
 * @returns {number|null} hoogte/breedte, of null bij ongeldige invoer
 */
function parseRatio(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().replace(/,/g, '.');
  if (!s) return null;
  if (s.includes(':')) {
    const parts = s.split(':').map((p) => parseFloat(p.trim()));
    if (parts.length !== 2 || !isFinite(parts[0]) || !isFinite(parts[1])) return null;
    const [w, h] = parts;
    if (w <= 0 || h <= 0) return null;
    return h / w;
  }
  const n = parseFloat(s);
  return isFinite(n) && n > 0 ? n : null;
}

/**
 * Parse een maat in cm (string uit een form-property; komma of punt).
 * @returns {number|null}
 */
function parseCm(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().replace(',', '.').replace(/\s*cm$/i, '');
  if (!s || !/^\d+(\.\d+)?$/.test(s)) return null;
  const n = parseFloat(s);
  return isFinite(n) && n > 0 ? n : null;
}

/** Hoogte in cm uit breedte + ratio, afgerond op 0,1 cm — overal dezelfde ronding. */
function heightFromWidth(widthCm, ratio) {
  return Math.round(widthCm * ratio * 10) / 10;
}

/** Oppervlakte in m² (breedte × hoogte in cm). */
function areaM2(widthCm, heightCm) {
  return (widthCm * heightCm) / 10000;
}

/**
 * Staffel-index voor een oppervlakte: eerste i waarvoor m2 < thresholds[i],
 * anders de laatste band. Randen (exact 0,5 / 1,0 / 2,5) vallen dus in de
 * duurdere band NIET — ze horen bij de goedkopere (>= drempel = volgende band).
 * NB: "m2 < threshold" betekent: exact 0,5 m² valt in band 2 (0,5–1).
 */
function tierIndex(m2, thresholds) {
  for (let i = 0; i < thresholds.length; i++) {
    if (m2 < thresholds[i]) return i;
  }
  return thresholds.length;
}

/**
 * Parse een kortingspercentage (product-metafield custom.discount_pct).
 * Geldig is 1 t/m 99; alles daarbuiten (leeg, 0, onzin) → 0 = geen korting.
 * @returns {number}
 */
function parsePct(raw) {
  const n = parseFloat(String(raw ?? '').replace(',', '.'));
  return isFinite(n) && n > 0 && n < 100 ? n : 0;
}

/**
 * Teksten die de functions zélf renderen: het achtervoegsel in de cartregel-
 * titel en de checkout-meldingen van de validation-function. Het thema doet
 * zijn eigen vertalingen via de theme-locales; deze twee kan het niet leveren,
 * omdat ze server-side ontstaan.
 *
 * Nederlands is de ingebouwde standaard. Andere talen komen uit
 * `table.i18n[<iso-code in kleine letters>]`, zodat een extra taal géén nieuwe
 * deploy van de wasm-functions nodig heeft — alleen een aanpassing van de
 * prijstabel (tools/set-i18n.mjs).
 */
const DEFAULT_STRINGS = {
  decimal_separator: ',',
  discount_suffix: 'korting',
  errors: {
    this_artwork: 'dit kunstwerk',
    no_table:
      'De prijs van "{title}" kan op dit moment niet berekend worden. Probeer het later opnieuw of neem contact op.',
    invalid: 'Kies eerst een geldige maat en materiaal voor "{title}" voordat je afrekent.',
    no_color: 'Kies een lijstkleur voor "{title}" voordat je afrekent.',
  },
};

/** Teksten voor één taal, met de Nederlandse standaard als terugval. */
function strings(table, isoCode) {
  const iso = String(isoCode ?? '').toLowerCase();
  const over = (table && table.i18n && table.i18n[iso]) || null;
  return {
    ...DEFAULT_STRINGS,
    ...(over || {}),
    errors: { ...DEFAULT_STRINGS.errors, ...((over && over.errors) || {}) },
  };
}

/** "…{title}…" invullen. */
function fillTitle(template, title) {
  return String(template).split('{title}').join(title);
}

/** Zoek een materiaal op key óf exact label. @returns {object|null} */
function findMaterial(table, keyOrLabel) {
  if (keyOrLabel == null) return null;
  const s = String(keyOrLabel).trim();
  return (
    table.materials.find((m) => m.key === s) ||
    table.materials.find((m) => m.label === s) ||
    null
  );
}

/**
 * Volledige berekening. Alle borging zit hier: hoogte wordt ALTIJD uit de
 * ratio herberekend (client-hoogte wordt genegeerd), breedte wordt geclampt
 * op min/max.
 *
 * @param {object} args
 * @param {object} args.table       prijstabel-JSON (zie data/prijstabel.json)
 * @param {string|number} args.width    breedte-invoer (cm)
 * @param {string} args.material    materiaal-key of -label
 * @param {string|number} args.ratio    ratio-metafield van het product
 * @param {number} [args.minCm]     override min breedte (product-metafield)
 * @param {number} [args.maxCm]     override max breedte (product-metafield)
 * @param {number} [args.discountPct]  korting in % (product-metafield custom.discount_pct)
 * @returns {{ok:true, widthCm:number, heightCm:number, m2:number, tier:number,
 *            ratePerM2:number, price:number, priceStr:string,
 *            priceOriginal:number, priceOriginalStr:string, discountPct:number,
 *            material:object}
 *          |{ok:false, error:string}}
 */
function calculatePrice({ table, width, material, ratio, minCm, maxCm, discountPct }) {
  const r = parseRatio(ratio);
  if (r == null) return { ok: false, error: 'invalid_ratio' };

  const mat = findMaterial(table, material);
  if (!mat) return { ok: false, error: 'invalid_material' };

  const w = parseCm(width);
  if (w == null) return { ok: false, error: 'invalid_width' };

  const min = isFinite(minCm) && minCm > 0 ? minCm : table.defaults.min_cm;
  const max = isFinite(maxCm) && maxCm > 0 ? maxCm : table.defaults.max_cm;
  if (w < min || w > max) return { ok: false, error: 'width_out_of_range' };

  const h = heightFromWidth(w, r);
  const m2 = areaM2(w, h);
  const tier = tierIndex(m2, table.thresholds_m2);
  const rate = mat.rates[tier];
  // Afronden op hele centen; als string met 2 decimalen voor de Function-output.
  const priceOriginal = Math.round(m2 * rate * 100) / 100;
  // Korting over de al op centen afgeronde basisprijs, opnieuw op centen
  // afgerond — dezelfde volgorde in function, thema en beheerscripts.
  const pct = parsePct(discountPct);
  const price = pct ? Math.round(priceOriginal * (100 - pct)) / 100 : priceOriginal;

  return {
    ok: true,
    widthCm: w,
    heightCm: h,
    m2,
    tier,
    ratePerM2: rate,
    price,
    priceStr: price.toFixed(2),
    priceOriginal,
    priceOriginalStr: priceOriginal.toFixed(2),
    discountPct: pct,
    material: mat,
  };
}

/**
 * "Vanaf"-prijs voor een product: kleinste maat × goedkoopste materiaal.
 * Gebruikt door set-artwork.mjs om de variantprijs te zetten.
 */
function fromPrice({ table, ratio, minCm, discountPct }) {
  const r = parseRatio(ratio);
  if (r == null) return null;
  const w = isFinite(minCm) && minCm > 0 ? minCm : table.defaults.min_cm;
  const h = heightFromWidth(w, r);
  const m2 = areaM2(w, h);
  const tier = tierIndex(m2, table.thresholds_m2);
  const cheapest = Math.min(...table.materials.map((m) => m.rates[tier]));
  const base = Math.round(m2 * cheapest * 100) / 100;
  const pct = parsePct(discountPct);
  return pct ? Math.round(base * (100 - pct)) / 100 : base;
}

window.HZPricing = { parseRatio, parseCm, heightFromWidth, areaM2, tierIndex, parsePct, DEFAULT_STRINGS, strings, fillTitle, findMaterial, calculatePrice, fromPrice };
})();
